import { ReactElement, useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import {
  PageWrapper,
  SectionTitle,
  EditableDropdown,
  FlexWrapper,
  DropdownWrapper,
  TableWrapper,
  TableHeader,
  Loader
} from '../../../components'
import strings from '../../../locale/en'
import { FormBackWrapper, TableRow } from '../User/subcomponents'
import { tableHeader } from './const'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getInstitutes } from '../../../redux/coachingCenter/actions'
import { RootState } from '../../../redux/store'
import {
  getBatchDropdown,
  getBranchDropdown,
  getCoursesDropdown,
  getInstituteDropdown
} from '../../../helpers'
import {
  getAllCoursesByInstitute,
  getChildCourses,
  resetChapters,
  resetCourses,
  resetSubjects,
  resetTopics
} from '../../../redux/course/action'
import {
  getBranchesForCourse,
  resetBranches
} from '../../../redux/branch/action'
import { getBatchesForCourse, resetBatches } from '../../../redux/batch/action'
import {
  getMaterialsWithPublishStatus,
  resetMaterial,
  selectedMaterial,
  unPublishMaterial
} from '../../../redux/materialAccess/action'
import { Material } from '../../../redux/materialAccess/types'
import format from 'date-fns/format'
import { DATE_FORMAT_MMDDYYYYHHMMSS } from '../../../const/dateFormat'
import { useHistory } from 'react-router-dom'
import ROUTES from '../../../const/routes'
import { DropdownListProps } from '../../../components/EditableDropdown/typings'
import { ROLE } from '../../../helpers/determineMenu'
import StyledButton from '../../../components/Button'

const PublishMaterial = (): ReactElement => {
  const {
    institutes = [],
    courses,
    branches,
    batches,
    subjects,
    chapters,
    topics,
    isLoading,
    materials,
    role,
    userDetail
  } = useSelector(
    (state: RootState) => ({
      institutes: state.coachingCenter.institutes,
      courses: state.course.courses,
      branches: state.branch.branches,
      batches: state.batch.batches,
      subjects: state.course.subjects,
      chapters: state.course.chapters,
      topics: state.course.topics,
      isLoading: state.material.isLoading,
      materials: state.material.materials,
      role: state.user.userInfo?.role,
      userDetail: state.user?.userInfo?.userDetail
    }),
    shallowEqual
  )

  const initialValues = {
    coachingCentreId: '',
    courseId: '',
    branchId: '',
    batchId: '',
    subjectId: '',
    chapterId: '',
    topicId: ''
  }
  const [values, setValues] = useState(initialValues)
  const history = useHistory()
  const dispatch = useDispatch()

  const teacherInstitutes = role === ROLE.TEACHER_ADMIN ? institutes.filter((data) => data?.coachingCentreName === userDetail?.coachingCentre.coachingCentreName) : institutes
  const teacherInstitutesLists = teacherInstitutes ? getInstituteDropdown(teacherInstitutes) : []
  const courseList = courses ? getCoursesDropdown(courses) : []
  const branchList = branches ? getBranchDropdown(branches) : []
  const batchList = batches ? getBatchDropdown(batches) : []
  const subjectList = subjects ? getCoursesDropdown(subjects) : []
  const chapterList = chapters ? getCoursesDropdown(chapters) : []
  const topicList = topics ? getCoursesDropdown(topics) : []
  const shouldHideInstitutes =
    role === ROLE.BRANCH_ADMIN || role === ROLE.INSTITUTE_ADMIN

  const {
    menu: {
      admin: {
        instituteName,
        selectCourse,
        selectBranch,
        selectBatch,
        selectSubject,
        selectChapter,
        selectTopic,
        publishMaterial,
        publish,
        unPublish,
        notPublishedYet
      }
    }
  } = strings

  const resetForm = () => {
    setValues(initialValues)
    resetValues()
  }

  const handlePublish = (material: Material) => {
    const { isPublished, batchId, materialId } = material
    if (isPublished) {
      dispatch(unPublishMaterial({ batchId, materialId }))
      resetForm()
    } else {
      dispatch(selectedMaterial({ ...values, materialId }))
      history.push(ROUTES.PUBLISH_MATERIAL_EDIT)
    }
  }

  const resetValues = () => {
    dispatch(resetBatches())
    dispatch(resetBranches())
    dispatch(resetMaterial())
    dispatch(resetCourses())
    dispatch(resetChapters())
    dispatch(resetSubjects())
    dispatch(resetTopics())
  }

  const handleInstituteSelect = (institue: DropdownListProps) => {
    dispatch(getAllCoursesByInstitute(institue?.id))
    setValues({ ...initialValues, coachingCentreId: institue?.id })
    resetValues()
  }

  const handleCourseSelect = (course: DropdownListProps) => {
    dispatch(
      getBranchesForCourse({
        coachingCentreId: values.coachingCentreId,
        courseId: course?.id
      })
    )
    dispatch(getChildCourses({ courseId: course?.id, type: 'SUBJECT' }))
    dispatch(resetBatches())
    dispatch(resetBranches())
    dispatch(resetMaterial())
    dispatch(resetChapters())
    dispatch(resetSubjects())
    dispatch(resetTopics())
    setValues({ ...values, courseId: course?.id })
  }

  const handleBranchSelect = (branch: DropdownListProps) => {
    dispatch(
      getBatchesForCourse({
        coachingCentreId: values.coachingCentreId,
        courseId: values.courseId,
        branchId: branch?.id
      })
    )
    dispatch(resetBatches())
    dispatch(resetMaterial())
    setValues({ ...values, branchId: branch?.id })
  }

  const handleBatchSelect = (batch: DropdownListProps) => {
    dispatch(resetMaterial())
    values?.topicId &&
      dispatch(
        getMaterialsWithPublishStatus({
          topicId: values?.topicId,
          batchId: values.batchId
        })
      )
    setValues({ ...values, batchId: batch?.id })
  }

  const handleSubjectSelect = (subject: DropdownListProps) => {
    setValues({ ...values, subjectId: subject?.id })
    dispatch(getChildCourses({ courseId: subject?.id, type: 'CHAPTER' }))
    dispatch(resetMaterial())
    dispatch(resetChapters())
    dispatch(resetTopics())
  }

  const handleChapterSelect = (chapter: DropdownListProps) => {
    setValues({ ...values, chapterId: chapter?.id })
    dispatch(getChildCourses({ courseId: chapter?.id, type: 'TOPIC' }))
    dispatch(resetMaterial())
    dispatch(resetTopics())
  }

  const handleTopicSelect = (topic: DropdownListProps) => {
    setValues({ ...values, topicId: topic?.id })
    dispatch(
      getMaterialsWithPublishStatus({
        topicId: topic?.id,
        batchId: values.batchId
      })
    )
    dispatch(resetMaterial())
  }

  useEffect(() => {
    if (shouldHideInstitutes) {
      const instituteId = userDetail?.coachingCenterId || ''
      dispatch(getAllCoursesByInstitute(instituteId))
      setValues({ ...initialValues, coachingCentreId: instituteId })
    } else {
      dispatch(getInstitutes())
      resetValues()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <PageWrapper id="container">
      <SectionTitle title={publishMaterial} hasBorder />
      <FlexWrapper>
        {!shouldHideInstitutes && (
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={teacherInstitutesLists || []}
              title={instituteName}
              placeholder={instituteName}
              isRequired
              defaultValue={{
                id: values?.coachingCentreId || '',
                name: values?.coachingCentreId
                  ? teacherInstitutesLists.filter(
                    (institue) => institue?.id === values?.coachingCentreId
                  )[0]?.name
                  : ''
              }}
              handleSelect={(institue) => handleInstituteSelect(institue)}
            />
          </DropdownWrapper>
        )}
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={courseList || []}
            title={selectCourse}
            placeholder={selectCourse}
            isRequired
            handleSelect={(course) => handleCourseSelect(course)}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={branchList || []}
            title={selectBranch}
            placeholder={selectBranch}
            isRequired
            handleSelect={(branch) => handleBranchSelect(branch)}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={batchList || []}
            title={selectBatch}
            placeholder={selectBatch}
            isRequired
            handleSelect={(batch) => handleBatchSelect(batch)}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={subjectList || []}
            title={selectSubject}
            placeholder={selectSubject}
            isRequired
            handleSelect={(subject) => handleSubjectSelect(subject)}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={chapterList || []}
            title={selectChapter}
            placeholder={selectChapter}
            isRequired
            handleSelect={(chapter) => handleChapterSelect(chapter)}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={topicList || []}
            title={selectTopic}
            placeholder={selectTopic}
            isRequired
            handleSelect={(topic) => handleTopicSelect(topic)}
          />
        </DropdownWrapper>
      </FlexWrapper>
      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <FormBackWrapper>
            <TableWrapper>
              <Table size="sm" responsive="sm">
                <TableHeader>
                  <TableRow>
                    {tableHeader?.map((header, index) => (
                      <th key={`publish-material-header-${index}`}>{header}</th>
                    ))}
                  </TableRow>
                </TableHeader>
                <tbody>
                  {Array.isArray(materials) &&
                    materials?.map((material: Material, index) => (
                      <TableRow key={`material-${index}`}>
                        <td>{material?.title}</td>
                        <td>
                          {material?.publishedDate
                            ? format(
                              new Date(material.publishedDate),
                              DATE_FORMAT_MMDDYYYYHHMMSS
                            )
                            : notPublishedYet}
                        </td>
                        <td>
                          <StyledButton
                            onClick={() => {
                              handlePublish(material)
                            }}
                            variant="outline-primary"
                          >
                            {material.isPublished ? unPublish : publish}
                          </StyledButton>
                        </td>
                      </TableRow>
                    ))}
                </tbody>
              </Table>
            </TableWrapper>
          </FormBackWrapper>
        </div>
      )}
    </PageWrapper>
  )
}

export default PublishMaterial
